import React, { useEffect, useState, useRef } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { Zap } from 'lucide-react';

const AnimatedXPCounter = ({ value, showIcon = true, className = '' }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const prevValue = useRef(value);
  
  // Spring animation for smooth counting
  const springValue = useSpring(value, { stiffness: 100, damping: 30 });
  const displaySpring = useTransform(springValue, (latest) => Math.round(latest));
  
  useEffect(() => {
    if (value !== prevValue.current) {
      setIsAnimating(true);
      springValue.set(value);
      
      const unsubscribe = displaySpring.on('change', (latest) => {
        setDisplayValue(latest);
      });
      
      // Stop animation after a while
      const timeout = setTimeout(() => {
        setIsAnimating(false);
      }, 1000);
      
      prevValue.current = value;
      
      return () => {
        unsubscribe();
        clearTimeout(timeout);
      };
    }
  }, [value, springValue, displaySpring]);

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {showIcon && (
        <motion.div
          animate={isAnimating ? { 
            scale: [1, 1.3, 1],
            rotate: [0, 10, -10, 0]
          } : {}}
          transition={{ duration: 0.4 }}
        >
          <Zap className={`w-4 h-4 ${isAnimating ? 'text-amber-400' : 'text-amber-500'}`} />
        </motion.div>
      )}
      <motion.span
        className={`font-bold tabular-nums ${isAnimating ? 'text-amber-400' : ''}`}
        animate={isAnimating ? { 
          scale: [1, 1.1, 1],
          textShadow: ['0 0 0px transparent', '0 0 10px rgba(251, 191, 36, 0.5)', '0 0 0px transparent']
        } : {}}
        transition={{ duration: 0.5 }}
      >
        {displayValue.toLocaleString()}
      </motion.span>
      <span className="text-muted-foreground text-sm">pts</span>
    </div>
  );
};

export default AnimatedXPCounter;
